// Používané hrúbky dosiek a šírka hrany (z NastaveniaPage, KOLO 170).
import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateBoardThickness,
  useDeleteBoardThickness,
  useBoardThicknesses,
  useUpdateBoardThickness,
} from "@/api/hooks/useBoardThicknesses";
import { useEdgeWidths } from "@/api/hooks/useCennik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BoardThickness } from "@/api/schemas";

// ─── Hrúbky dosiek + priradenie šírky hrany ─────────────────

/** Sekcia "Hrúbky dosiek". CRUD nad /api/board-thicknesses. */
export function BoardThicknessesSection(): JSX.Element {
  const query = useBoardThicknesses();
  const deleteMutation = useDeleteBoardThickness();
  const updateFlagMutation = useUpdateBoardThickness();
  const [editing, setEditing] = useState<BoardThickness | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const items = query.data?.data ?? [];

  async function toggleAutoHrany(b: BoardThickness): Promise<void> {
    try {
      await updateFlagMutation.mutateAsync({
        id: b.id,
        data: { auto_hrany: !b.auto_hrany },
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(b: BoardThickness): Promise<void> {
    if (!window.confirm(`Zmazať hrúbku ${b.hrubka_mm} mm?`)) return;
    try {
      await deleteMutation.mutateAsync(b.id);
      toast({ title: "Hrúbka zmazaná" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl">Používané hrúbky dosiek</h2>
          <p className="text-xs text-muted-foreground">
            Zoznam hrúbok, ktoré naša firma reálne používa — <strong>z tohto
            zoznamu sa ponúkajú hrúbky pri dielcoch</strong> (spolu s
            lepenými z pravidiel nižšie). Pre každú hrúbku je definovaná
            šírka hrany — pri auto-priradení sa hrana vyberie podľa tohto
            pravidla.
            <br />
            <strong>Auto hrany</strong>: len zaškrtnuté hrúbky (default
            10/18/25/36) dostávajú automaticky detegované hrany v materiáloch zákazky —
            ostatné hrúbky si v materiáli pridáš ručne cez „+ Pridať hrúbku".
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" /> Pridať
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hrúbka dosky</TableHead>
              <TableHead>Šírka hrany</TableHead>
              <TableHead>Nadmiera/stranu</TableHead>
              <TableHead className="text-center">Auto hrany</TableHead>
              <TableHead>Popis</TableHead>
              <TableHead className="w-24 text-right">Akcie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  Žiadne hrúbky. Pridaj cez "+ Pridať".
                </TableCell>
              </TableRow>
            ) : (
              items.map((b) => {
                const nadmiera = (b.edge_width_mm - b.hrubka_mm) / 2;
                return (
                  <TableRow key={b.id}>
                    <TableCell className="font-mono">{b.hrubka_mm} mm</TableCell>
                    <TableCell className="font-mono">{b.edge_width_mm} mm</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {nadmiera > 0
                        ? `+${nadmiera.toFixed(1)} mm/strana`
                        : nadmiera < 0
                          ? `${nadmiera.toFixed(1)} mm/strana (kratšia)`
                          : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        checked={b.auto_hrany}
                        onChange={() => void toggleAutoHrany(b)}
                        disabled={updateFlagMutation.isPending}
                        aria-label={`Auto hrany pre ${b.hrubka_mm} mm`}
                        title="Automaticky detegovať hrany pre túto hrúbku"
                        className="h-4 w-4 accent-primary"
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {b.popis ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(b)}
                        aria-label="Upraviť"
                        title="Upraviť hrúbku dosky"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void handleDelete(b)}
                        aria-label="Zmazať"
                        title="Zmazať hrúbku dosky"
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <BoardThicknessDialog
        open={createOpen || editing !== null}
        thickness={editing}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
      />
    </section>
  );
}

/** Zoskupí šírky hrán podľa pásma pre dropdown. */
const PASMA_PORADIE = ["UZKA", "STREDNA", "SIRSIA", "SIROKA", "BLOK"] as const;

function BoardThicknessDialog({
  open,
  thickness,
  onOpenChange,
}: {
  open: boolean;
  thickness: BoardThickness | null;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const isEdit = !!thickness;
  const createMutation = useCreateBoardThickness();
  const updateMutation = useUpdateBoardThickness();

  const [hrubkaInput, setHrubkaInput] = useState("");
  const [edgeWidthInput, setEdgeWidthInput] = useState("");
  const [popisInput, setPopisInput] = useState("");
  const widthsQuery = useEdgeWidths();
  const widths = widthsQuery.data?.data ?? [];

  useEffect(() => {
    if (open) {
      setHrubkaInput(thickness ? String(thickness.hrubka_mm) : "");
      setEdgeWidthInput(thickness ? String(thickness.edge_width_mm) : "");
      setPopisInput(thickness?.popis ?? "");
    }
  }, [open, thickness]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const hrubka = parseInt(hrubkaInput, 10);
    const width = parseInt(edgeWidthInput, 10);
    if (!Number.isFinite(hrubka) || hrubka <= 0) {
      toast({
        variant: "destructive",
        title: "Neplatná hrúbka",
        description: "Zadaj kladné celé číslo (mm).",
      });
      return;
    }
    if (!Number.isFinite(width) || width <= 0) {
      toast({
        variant: "destructive",
        title: "Neplatná šírka hrany",
        description: "Zadaj kladné celé číslo (mm).",
      });
      return;
    }
    try {
      if (isEdit && thickness) {
        await updateMutation.mutateAsync({
          id: thickness.id,
          data: {
            edge_width_mm: width,
            popis: popisInput || null,
          },
        });
        toast({ title: "Hrúbka uložená" });
      } else {
        await createMutation.mutateAsync({
          hrubka_mm: hrubka,
          edge_width_mm: width,
          auto_hrany: false,
          popis: popisInput || null,
        });
        toast({ title: "Hrúbka pridaná" });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  const nadmiera =
    Number.isFinite(parseInt(edgeWidthInput, 10)) &&
    Number.isFinite(parseInt(hrubkaInput, 10))
      ? (parseInt(edgeWidthInput, 10) - parseInt(hrubkaInput, 10)) / 2
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Upraviť hrúbku ${thickness?.hrubka_mm} mm`
              : "Pridať hrúbku dosky"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Hrúbka dosky a hrana, ktorá sa k nej priradí automaticky.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bt-hrubka">Hrúbka dosky (mm)</Label>
              <Input
                id="bt-hrubka"
                type="number"
                min={1}
                value={hrubkaInput}
                onChange={(e) => setHrubkaInput(e.target.value)}
                placeholder="18"
                disabled={isEdit}
                autoFocus={!isEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bt-edge">Šírka hrany (z cenníka)</Label>
              <Select
                value={edgeWidthInput}
                onValueChange={setEdgeWidthInput}
              >
                <SelectTrigger id="bt-edge" autoFocus={isEdit}>
                  <SelectValue placeholder="Vybrať šírku…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {PASMA_PORADIE.map((pasmoKod) => {
                    const inPasmo = widths.filter(
                      (w) => w.pasmo_kod === pasmoKod,
                    );
                    if (inPasmo.length === 0) return null;
                    const label = inPasmo[0]?.pasmo_label ?? pasmoKod;
                    return (
                      <SelectGroup key={pasmoKod}>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                          {label}
                        </div>
                        {inPasmo.map((w) => (
                          <SelectItem
                            key={w.sirka_mm}
                            value={String(w.sirka_mm)}
                          >
                            <span className="font-mono">{w.sirka_mm} mm</span>
                            <span className="text-muted-foreground text-xs ml-2">
                              · {w.pocet_hran}× v cenníku
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          {nadmiera !== null && nadmiera !== 0 ? (
            <p className="text-xs text-muted-foreground">
              Nadmiera{" "}
              <span className="font-mono font-medium">
                {nadmiera > 0 ? "+" : ""}
                {nadmiera.toFixed(1)} mm
              </span>{" "}
              na stranu — {nadmiera > 0
                ? "hrana presahuje, zafrézuje sa"
                : "hrana je užšia ako doska"}
              .
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="bt-popis">Popis (voliteľné)</Label>
            <Input
              id="bt-popis"
              value={popisInput}
              onChange={(e) => setPopisInput(e.target.value)}
              placeholder="DTD štandard / Pracovná doska / …"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Zrušiť
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Ukladá sa…"
                : isEdit
                  ? "Uložiť"
                  : "Pridať"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
